import React from 'react';
import { useParams } from 'react-router-dom';
import {
  SelfOnboardingAbortReason,
  SelfOnboardingWorkerAction,
  useSelfOnboardingAssignToJobsiteMutation,
  useUpdateWorkerMutation,
} from 'apollo/generated/client-operations';
import { Form, FormOnSubmit } from 'components/form';
import { AlertService } from 'components/alertService';
import { LoadingError } from 'components/loadingError';
import { getGraphQLError } from 'utils/error';
import { isStringEnumValue } from 'utils';
import { commonClasses } from 'containers/selfOnboarding/steps/common.style';
import {
  SelfOnboardingContractorSelectionFormData,
  SelfOnboardingStepKey,
  SelfOnboardingStepProps,
} from 'containers/selfOnboarding/steps/types';
import { Copy } from 'containers/selfOnboarding/steps/utils';
import {
  StepActions,
  StepInfo,
  StepInfoTitle,
  StepJobsiteInfo,
} from 'containers/selfOnboarding/steps/components';
import { useSelfOnboardingFormCommonProps } from 'containers/selfOnboarding/helpers/forms';
import { getFormInputsHook } from 'containers/selfOnboarding/steps/contractorSelectionStep/ContractorSelectionStep.forms';
import { ErrorStep } from 'containers/selfOnboarding/steps/errorStep';

/** Lifecycle of the Mutation B assignment this step runs. */
type AssignStatus = 'idle' | 'saving' | 'failed';

/**
 * Contractor-selection step of the authenticated flow (Mutation B). Binds the
 * (already authenticated) worker to the jobsite for the selected contractor and
 * resolves any resume/abort conflict — the convergence point for both new and
 * existing workers.
 *
 * When the jobsite does not require an explicit contractor choice, the step
 * auto-assigns the invitation's contractor and advances without rendering a
 * picker.
 */
export function AuthContractorSelectionStep(
  props: SelfOnboardingStepProps,
): React.ReactElement {
  const {
    state,
    updateState,
    navigation,
    stepConfig,
    localize,
    language,
    jobsiteInvitation,
    refetchData,
  } = props;
  const {
    jobsiteWorker,
    contractorOptions,
    contractorId,
    shouldSelectContractor,
  } = state;
  const { jobsiteInvitationId } = useParams<{ jobsiteInvitationId: string }>();
  const {
    goToStep,
    goToNextStep,
    getStepIndex,
    updateQueryParams,
    state: navigationState,
  } = navigation;
  const { autoFocusField } = navigationState;
  const { contractor } = jobsiteInvitation?.jobsiteContractor ?? {};

  const [assignStatus, setAssignStatus] = React.useState<AssignStatus>('idle');
  const isSaving = assignStatus === 'saving';
  const [isContinueActionEnabled, setIsContinueActionEnabled] = React.useState(
    !!contractorId,
  );
  // `contractorId` (from a URL param, or the invitation-backed state) can arrive
  // a render or two after this step mounts in the authenticated flow, where the
  // full session state is built only after sign-in. The form hook reports
  // enablement only on user changes (useDidUpdateEffect skips mount), so without
  // this a pre-selected contractor would leave Continue stuck disabled. Enable
  // it once a contractor is present.
  React.useEffect(() => {
    if (contractorId) setIsContinueActionEnabled(true);
  }, [contractorId]);
  const [assignToJobsite] = useSelfOnboardingAssignToJobsiteMutation();
  const [updateWorker] = useUpdateWorkerMutation();

  const assignAndRoute = async (
    selectedContractorId?: string,
  ): Promise<void> => {
    // Capture the worker's persisted step *before* Mutation B — it
    // unconditionally re-stamps the step to BasicInfo server-side, so this is
    // the only point where the real "left off" step is still readable. On a
    // retry this is still the state from before the first attempt: nothing
    // refetches in between.
    const resumeWorker = state.worker;
    const resumeStep = resumeWorker?.selfOnboardingCurrentStep;
    // The saved step is worker-wide, not per site: it only describes this site
    // when the worker was already assigned here. Without that, a resume can only
    // come from a failed earlier attempt that still created the jobsiteWorker.
    const wasAssignedToSite = !!state.jobsiteWorker;

    const { data } = await assignToJobsite({
      variables: {
        input: { jobsiteInvitationId, contractorId: selectedContractorId },
      },
    });
    const { selfOnboardingInfo } = data.selfOnboardingAssignToJobsite ?? {};
    const { continueAction, abortReason } = selfOnboardingInfo ?? {};

    if (continueAction === SelfOnboardingWorkerAction.AbortOnboarding) {
      goToStep(
        abortReason ===
          SelfOnboardingAbortReason.JobsiteWorkerWithDifferentContractor
          ? SelfOnboardingStepKey.JobsiteWorkerWithDifferentContractor
          : SelfOnboardingStepKey.AlreadyCompleted,
      );
      return;
    }

    // Resuming: return the worker to the exact step they left off on. Mutation
    // B just re-stamped the persisted step to BasicInfo, so re-persist the real
    // step (this also drives the nav guard on any later refresh) and navigate
    // there directly instead of walking forward from contractor selection.
    // Only a step past this one is a real resume point: this step or an
    // earlier one would route back to itself (an endless spinner on
    // auto-assign links, PEN-109) or backwards, so walk forward instead.
    if (
      continueAction === SelfOnboardingWorkerAction.ResumeOnboarding &&
      wasAssignedToSite &&
      resumeWorker?.workerId &&
      isStringEnumValue(SelfOnboardingStepKey, resumeStep) &&
      getStepIndex(resumeStep) >
        getStepIndex(SelfOnboardingStepKey.ContractorSelection)
    ) {
      await updateWorker({
        variables: {
          workerId: resumeWorker.workerId,
          workerInput: { selfOnboardingCurrentStep: resumeStep },
        },
      });
      await refetchData();
      goToStep(resumeStep);
      return;
    }

    // Refetch so the session rebuilds full onboarding state (incl. the new
    // jobsiteWorker + isAssignedToMultipleJobsites) rather than threading the
    // mutation's minimal selection into state. StartNewOnboarding then simply
    // proceeds to the first jobsite-data step.
    await refetchData();
    goToNextStep();
  };

  // The full invitation (hence `shouldSelectContractor` / `contractorOptions`)
  // loads only after sign-in — a render or two AFTER this step mounts when
  // arriving straight from OTP. Until it resolves, the state still holds the
  // minimal pre-account seed (`shouldSelectContractor: false`), so we must not
  // decide yet: auto-assigning the default contractor prematurely creates a
  // jobsiteWorker under it and dead-ends any later "go back + pick a real
  // contractor" with a JobsiteWorkerWithDifferentContractor abort.
  // `contractorOptions` is populated only by the full state build, so its
  // presence is the "contractor info resolved" signal.
  const isContractorInfoResolved = !!contractorOptions;
  // Once resolved, skip the picker (assign the invitation's contractor) only
  // when the jobsite doesn't require an explicit choice.
  const autoAssign = isContractorInfoResolved && !shouldSelectContractor;

  /** Runs Mutation B and routes on; resolves to whether it succeeded. */
  const assign = async (selectedContractorId?: string): Promise<boolean> => {
    setAssignStatus('saving');
    try {
      await assignAndRoute(selectedContractorId);
      setAssignStatus('idle');
      return true;
    } catch (error) {
      setAssignStatus('failed');
      AlertService.alert(
        'danger',
        'Something went wrong!',
        getGraphQLError(error),
      );
      return false;
    }
  };

  // Start auto-assign when it becomes possible, at most once per mount, so the
  // effect never sends Mutation B on its own again. Retries after a failure go
  // through the retry action below; nothing on the server prevents a duplicate
  // jobsiteWorker if a retry overlaps a request still in flight. The
  // invitation's contractor is loaded by the time `autoAssign` turns true.
  const hasAutoAssignStartedRef = React.useRef(false);
  React.useEffect(() => {
    if (!autoAssign || hasAutoAssignStartedRef.current) return;
    hasAutoAssignStartedRef.current = true;
    assign(contractor?.contractorId);
  }, [autoAssign]);

  const onSubmit: FormOnSubmit<
    SelfOnboardingContractorSelectionFormData
  > = async (data, event): Promise<void> => {
    if (isSaving) return;
    updateState({ ...data });
    updateQueryParams({ contractorId: data.contractorId.value });
    if (!(await assign(data.contractorId.value))) {
      event.preventDefault();
    }
  };

  const inputs = getFormInputsHook({
    localize,
    contractorOptions,
    setIsContinueActionEnabled,
  });
  const defaultValues = React.useMemo(() => ({ contractorId }), [contractorId]);
  const { fieldsConfig, localization } = useSelfOnboardingFormCommonProps(
    stepConfig,
    localize,
  );

  if (autoAssign && assignStatus === 'failed') {
    return (
      <>
        <ErrorStep localize={localize} />
        <StepActions
          jobsiteInvitation={jobsiteInvitation}
          localize={localize}
          actions="continue"
          onContinue={(): void => {
            assign(contractor?.contractorId);
          }}
        />
      </>
    );
  }

  // Wait for the contractor info to resolve, and cover the auto-assign in-flight
  // state, with a loader — never flash the picker before we know it's needed.
  if (!isContractorInfoResolved || autoAssign) {
    return <LoadingError loading />;
  }

  return (
    <div className={commonClasses.stepContainer}>
      <StepInfo>
        <StepInfoTitle
          title={`${localize(Copy.self_onboarding_worker_match_header)} 👋`}
          subtitle={localize(Copy.self_onboarding_worker_match_instructions)}
          withBottomBorder
          hideOnMobile
        />
        <StepJobsiteInfo
          jobsiteInvitation={jobsiteInvitation}
          jobsiteWorker={jobsiteWorker}
        />
      </StepInfo>
      <Form
        inputs={inputs}
        defaultValues={defaultValues}
        onSubmit={onSubmit}
        autoFocus={
          (autoFocusField as keyof SelfOnboardingContractorSelectionFormData) ??
          true
        }
        renderBelow={
          <StepActions
            jobsiteInvitation={jobsiteInvitation}
            localize={localize}
            actions="continue"
            continueActionEnabled={isContinueActionEnabled}
            continueActionWithSpinner={isSaving}
          />
        }
        validationTriggers={[language]}
        className={commonClasses.form}
        inputsContainerClassName={commonClasses.formInputsContainer}
        fieldsConfig={fieldsConfig}
        localization={localization}
      />
    </div>
  );
}
